import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { FaChartLine, FaSkullCrossbones, FaFire, FaUsers, FaSnowflake, FaRocket } from 'react-icons/fa';

const Story = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const titleVariants = {
    hidden: {
      opacity: 0,
      scale: 0.8,
      rotateX: -90
    },
    visible: {
      opacity: 1,
      scale: 1,
      rotateX: 0,
      transition: {
        duration: 1,
        ease: [0.6, 0.05, 0.01, 0.9],
      }
    }
  };

  const timelineLineVariants = {
    hidden: { height: 0 },
    visible: {
      height: "100%",
      transition: {
        duration: 2,
        ease: "easeInOut",
        delay: 0.5
      }
    }
  };

  const timelineEvents = [
    {
      icon: FaChartLine,
      iconColor: "text-rizzmas-green",
      bgGradient: "from-rizzmas-green/20 to-rizzmas-green/5",
      borderColor: "border-rizzmas-green/30",
      title: "December 2023: The Birth",
      subtitle: "The charts went absolutely parabolic",
      description: "Last Christmas, $RIZZMAS exploded onto the scene. TikTok was flooded with memes, Twitter was on fire, and the charts? Pure vertical action. Everyone wanted a piece of the Christmas magic.",
      stat: "1000x",
      statLabel: "Peak ROI",
      direction: "left"
    },
    {
      icon: FaSkullCrossbones,
      iconColor: "text-rizzmas-red",
      bgGradient: "from-rizzmas-red/20 to-rizzmas-red/5",
      borderColor: "border-rizzmas-red/30",
      title: "January 2024: The Fade",
      subtitle: "Like every seasonal token before it",
      description: "Then came January. The hype faded. The chart bled. Another Christmas coin lost to the crypto graveyard. The suits moved on. But the real believers? They never forgot.",
      stat: "-95%",
      statLabel: "The Dump",
      direction: "right"
    },
    {
      icon: FaFire,
      iconColor: "text-rizzmas-gold",
      bgGradient: "from-rizzmas-gold/20 to-rizzmas-gold/5",
      borderColor: "border-rizzmas-gold/30",
      title: "Throughout 2024: The Ember",
      subtitle: "The spark that never died",
      description: "While normies forgot, the community remembered. Every meme shared. Every inside joke. Every 'remember when?' — it all kept the flame alive. This wasn't just about money anymore. It was about the Rizz.",
      stat: "10K+",
      statLabel: "Diamond Hands",
      direction: "left"
    },
    {
      icon: FaUsers,
      iconColor: "text-rizzmas-green",
      bgGradient: "from-rizzmas-green/20 to-rizzmas-cream/5",
      borderColor: "border-rizzmas-green/30",
      title: "November 2024: The Gathering",
      subtitle: "The OGs return",
      description: "As Christmas 2024 approached, something magical happened. The original degens started DMing. 'What if we brought it back?' they asked. Not as a pump and dump. But as a community takeover. A movement.",
      stat: "11",
      statLabel: "OG Donors",
      direction: "right"
    },
    {
      icon: FaRocket,
      iconColor: "text-rizzmas-red",
      bgGradient: "from-rizzmas-red/20 to-rizzmas-green/20",
      borderColor: "border-rizzmas-gold/50",
      title: "December 2024: The Takeover",
      subtitle: "You are here. Right now.",
      description: "This time, there's no anonymous dev to rug you. No VC to dump on you. No corporate roadmap that means nothing. Just real believers with real capital building something legendary. RizzmasCTO isn't a coin — it's revenge.",
      stat: "$110K",
      statLabel: "War Chest",
      direction: "left"
    }
  ];

  return (
    <section ref={ref} className="relative py-40 px-4 overflow-hidden">
      {/* Subtle Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-rizzmas-gold/3 to-transparent" />

      <div className="max-w-6xl mx-auto relative z-10">
        {/* Title with 3D entrance */}
        <motion.div
          variants={titleVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="text-center mb-16 perspective-1000"
        >
          <h2 className="text-5xl md:text-7xl font-black mb-4 bg-gradient-to-r from-rizzmas-cream via-rizzmas-gold to-rizzmas-red bg-clip-text text-transparent">
            The Legend of Rizzmas
          </h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ delay: 0.5, duration: 0.8 }}
            className="text-xl md:text-2xl text-rizzmas-cream/70 max-w-3xl mx-auto"
          >
            A tale of hype, heartbreak, and the comeback of a lifetime
          </motion.p>
        </motion.div>

        {/* Timeline */}
        <div className="relative space-y-16">
          {/* Vertical line */}
          <motion.div
            className="absolute left-8 md:left-12 top-0 w-px bg-gradient-to-b from-rizzmas-gold/20 via-rizzmas-gold/10 to-transparent origin-top"
            variants={timelineLineVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
          />

          {timelineEvents.map((event, index) => {
            const IconComponent = event.icon;

            // Simplified animation
            const cardVariants = {
              hidden: {
                opacity: 0,
                y: 30
              },
              visible: {
                opacity: 1,
                y: 0,
                transition: {
                  duration: 0.6,
                  delay: index * 0.15 + 0.5,
                  ease: "easeOut"
                }
              }
            };

            const iconVariants = {
              hidden: {
                scale: 0,
                opacity: 0
              },
              visible: {
                scale: 1,
                opacity: 1,
                transition: {
                  duration: 0.4,
                  delay: index * 0.15 + 0.6,
                  ease: "easeOut"
                }
              }
            };

            return (
              <motion.div
                key={index}
                variants={cardVariants}
                initial="hidden"
                animate={isInView ? "visible" : "hidden"}
                className="relative"
              >
                {/* Timeline dot with spring animation */}
                <div className="absolute left-8 md:left-12 transform -translate-x-1/2 z-10">
                  <motion.div
                    variants={iconVariants}
                    className={`w-14 h-14 rounded-full bg-gradient-to-br ${event.bgGradient} border ${event.borderColor} flex items-center justify-center`}
                  >
                    <IconComponent className={`text-xl ${event.iconColor}`} />
                  </motion.div>
                </div>

                {/* Content card */}
                <div className={`ml-20 md:ml-32 bg-rizzmas-dark/40 backdrop-blur-sm border ${event.borderColor} rounded-xl p-6 md:p-8`}>
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-4">
                    <div>
                      <motion.h3
                        className={`text-2xl md:text-3xl font-black ${event.iconColor} mb-2`}
                        initial={{ opacity: 0, x: -20 }}
                        animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
                        transition={{ delay: index * 0.2 + 1.2 }}
                      >
                        {event.title}
                      </motion.h3>
                      <motion.p
                        className="text-lg text-rizzmas-cream/60 italic"
                        initial={{ opacity: 0 }}
                        animate={isInView ? { opacity: 1 } : { opacity: 0 }}
                        transition={{ delay: index * 0.2 + 1.4 }}
                      >
                        {event.subtitle}
                      </motion.p>
                    </div>
                    <motion.div
                      className="flex-shrink-0"
                      initial={{ scale: 0, opacity: 0 }}
                      animate={isInView ? { scale: 1, opacity: 1 } : { scale: 0, opacity: 0 }}
                      transition={{
                        duration: 0.5,
                        delay: index * 0.15 + 0.7,
                        ease: "easeOut"
                      }}
                    >
                      <div className={`bg-gradient-to-br ${event.bgGradient} border ${event.borderColor} rounded-xl px-6 py-3 text-center`}>
                        <div className={`text-3xl font-black ${event.iconColor}`}>
                          {event.stat}
                        </div>
                        <div className="text-sm text-rizzmas-cream/60 font-semibold">
                          {event.statLabel}
                        </div>
                      </div>
                    </motion.div>
                  </div>
                  <motion.p
                    className="text-lg md:text-xl text-rizzmas-cream/80 leading-relaxed"
                    initial={{ opacity: 0, y: 10 }}
                    animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
                    transition={{ delay: index * 0.2 + 1.5 }}
                  >
                    {event.description}
                  </motion.p>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Call to action */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{
            delay: timelineEvents.length * 0.15 + 1,
            duration: 0.6,
            ease: "easeOut"
          }}
          className="mt-20 text-center"
        >
          <div className="bg-gradient-to-r from-rizzmas-red/20 via-rizzmas-gold/20 to-rizzmas-green/20 border-2 border-rizzmas-gold/50 rounded-3xl p-8 md:p-12 relative">
            <p className="text-3xl md:text-4xl font-black text-rizzmas-gold mb-4 relative z-10">
              Last year you watched. This year you build.
            </p>
            <p className="text-xl md:text-2xl text-white font-semibold mb-4 relative z-10">
              This is your chance to be part of crypto history.
            </p>
            <div className="flex items-center justify-center gap-2 text-rizzmas-cream/60 relative z-10 flex-wrap">
              <FaSnowflake className="text-xl" />
              <p className="text-lg">The OGs are backing it. The believers are funding it. The degens are building it.</p>
              <FaSnowflake className="text-xl" />
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Story;
